/**
 * Aiuto — Centro di aiuto "a prova di stupido".
 *
 * iter v29 r233 — User M1389: «Le guide ok, ma devono essere a prova di
 * stupido. Niente WhatsApp, non posso rispondere io. L'assistente nel caso
 * può spiegare lui.» Linea editoriale chiara:
 *   - 6 mini-guide passo-passo (max 4 step ciascuna, parole semplici)
 *   - Ogni guida ha "Chiedi all'Assistente" → apre /dashboard/assistente?q=…
 *   - Bottone "Rivedi il tour iniziale" centralizzato
 *   - NIENTE WhatsApp / numeri / email visibili (richiesta esplicita)
 */
import React from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import {
  House, Microphone, Camera, ShareNetwork, WhatsappLogo, Storefront,
  ArrowRight, Sparkle, ChatCircleDots,
} from "@phosphor-icons/react";

const STORAGE_KEY = "pronto_onboarding_magico";

const GUIDES = [
  {
    id: "primo-annuncio",
    icon: House,
    title: "Pubblicare il primo annuncio",
    summary: "Crea l'annuncio in 3 minuti e mettilo online.",
    steps: [
      "Apri «I miei annunci» nel menu a sinistra.",
      "Clicca «Crea annuncio» e scrivi titolo, prezzo e città.",
      "Trascina dentro le foto (anche dal cellulare).",
      "Premi «Pubblica»: l'annuncio è online con il tuo logo.",
    ],
    question: "Come pubblico il mio primo annuncio su Pronto, passo per passo?",
  },
  {
    id: "sopralluogo",
    icon: Microphone,
    title: "Sopralluogo vocale dal cellulare",
    summary: "Cammini con il cliente, parli e scatti. Esci pronto.",
    steps: [
      "Apri «Sopralluogo live» dal menu o dal tab in basso (su iPhone).",
      "Tieni premuto il pulsante microfono e racconta cosa vedi.",
      "Scatta le foto della casa con il cellulare.",
      "Esci dalla visita: bozza annuncio e mappa servizi sono pronte nell'app.",
    ],
    question: "Come funziona il sopralluogo vocale di Pronto?",
  },
  {
    id: "foto",
    icon: Camera,
    title: "Migliorare le foto della casa",
    summary: "Luce, linee storte e divani in ordine. In automatico.",
    steps: [
      "Apri un annuncio già creato e vai sulla scheda «Foto».",
      "Carica le foto fatte al volo con il cellulare.",
      "Pronto sistema luce, prospettiva e ordine in pochi secondi.",
      "Confronta «prima/dopo» e salva. Pronto le mette nell'annuncio.",
    ],
    question: "Come faccio a migliorare le foto di un annuncio?",
  },
  {
    id: "social",
    icon: ShareNetwork,
    title: "Post, carosello e reel social",
    summary: "Brochure, post Instagram e reel coordinati in un click.",
    steps: [
      "Vai su un annuncio e apri «Editor social».",
      "Scegli lo stile (Elegante, Lussuoso, Moderno, Minimal) e il colore.",
      "Premi «Genera»: post, carosello e reel sono pronti col tuo logo.",
      "Premi «Salva» o «Condividi» per scaricarli o aprire WhatsApp.",
    ],
    question: "Come creo un post Instagram per un annuncio?",
  },
  {
    id: "whatsapp",
    icon: WhatsappLogo,
    title: "Messaggi WhatsApp ai contatti",
    summary: "Auguri o saluti a tutta la rubrica, personalizzati uno per uno.",
    steps: [
      "Apri «Messaggi WhatsApp» dal menu.",
      "Scegli a chi mandare (rubrica intera, gruppo, singolo cliente).",
      "Scrivi il messaggio modello (nome e città vengono adattati a ciascuno).",
      "Premi «Apri WhatsApp»: ogni messaggio è pronto, tu premi solo «Invia».",
    ],
    question: "Come mando un messaggio WhatsApp a tutti i miei contatti insieme?",
  },
  {
    id: "stima-cliente",
    icon: Storefront,
    title: "Pagina pubblica per ricevere stime",
    summary: "Una landing con il tuo nome dove i proprietari chiedono una stima.",
    steps: [
      "Vai su «Calcolo stima» nel menu, poi «Imposta pagina stima».",
      "Inserisci nome agenzia, foto, telefono e un breve testo di presentazione.",
      "Copia il link della tua pagina (es. prontoimmo.it/p/tuo-nome).",
      "Condividilo nei tuoi profili: ogni richiesta arriva in «Contatti» con tutti i dettagli.",
    ],
    question: "Come configuro la pagina pubblica di stima per i clienti?",
  },
];

function GuideCard({ guide }) {
  const Icon = guide.icon;
  const askUrl = `/dashboard/assistente?q=${encodeURIComponent(guide.question)}`;
  return (
    <article
      className="bg-white border border-[#E5E7E0] p-6 sm:p-7"
      data-testid={`guide-${guide.id}`}
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 w-11 h-11 rounded-full bg-[#F1F4ED] flex items-center justify-center">
          <Icon size={20} weight="duotone" className="text-[#7C8B6E]" />
        </div>
        <div className="flex-1 min-w-0">
          <h3
            className="font-editorial text-[22px] sm:text-2xl tracking-tight text-[#1C1C1A] leading-tight"
            data-testid={`guide-${guide.id}-title`}
          >
            {guide.title}
          </h3>
          <p className="mt-1 text-[14px] text-[#6B6A65]">{guide.summary}</p>
        </div>
      </div>

      <ol className="mt-5 space-y-3" data-testid={`guide-${guide.id}-steps`}>
        {guide.steps.map((step, i) => (
          <li key={i} className="flex gap-3 text-[14.5px] text-[#1C1C1A] leading-relaxed">
            <span className="shrink-0 font-editorial text-[#C8A66B] tabular-nums w-5 text-base">
              {i + 1}
            </span>
            <span>{step}</span>
          </li>
        ))}
      </ol>

      <div className="mt-5 pt-4 border-t border-[#EAE7DF]">
        <Link
          to={askUrl}
          className="inline-flex items-center gap-1.5 text-[13.5px] text-[#7C8B6E] hover:text-[#5e6c52] font-medium"
          data-testid={`guide-${guide.id}-ask`}
        >
          <ChatCircleDots size={15} weight="duotone" />
          Chiedi all{"'"}Assistente
          <ArrowRight size={13} weight="bold" />
        </Link>
      </div>
    </article>
  );
}

export default function Aiuto() {
  const restartTour = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch (_e) { /* */ }
    window.location.href = "/dashboard?onboarding=1";
  };

  return (
    <AppShell title="Aiuto">
      <div className="space-y-8" data-testid="aiuto-page">
        {/* Header */}
        <header className="max-w-2xl">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#6B6A65] font-semibold mb-2">
            Aiuto
          </div>
          <h1
            className="font-editorial text-4xl lg:text-5xl tracking-tighter leading-[1.05] text-[#1C1C1A]"
            data-testid="aiuto-title"
          >
            Come si fa cosa.
          </h1>
          <p className="mt-3 text-[15px] text-[#6B6A65] leading-relaxed">
            Sei guide brevi per le cose che farai spesso. Se non basta,
            l{"'"}Assistente di Pronto risponde a tutto, in italiano, in chat.
          </p>
        </header>

        {/* CTA Riavvia tour + Assistente */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={restartTour}
            className="bg-[#F1F4ED] hover:bg-[#E7EBE0] border border-[#7C8B6E]/30 p-5 text-left transition-colors"
            data-testid="aiuto-restart-tour"
          >
            <div className="flex items-center gap-2.5 mb-1">
              <Sparkle size={16} weight="fill" className="text-[#7C8B6E]" />
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#6B6A65] font-semibold">
                Tour iniziale
              </div>
            </div>
            <div className="font-editorial text-xl text-[#1C1C1A] tracking-tight">
              Rivedi le 6 schermate di benvenuto
            </div>
            <div className="mt-1 text-[13px] text-[#6B6A65]">
              60 secondi per ricapitolare cosa fa Pronto.
            </div>
          </button>

          <Link
            to="/dashboard/assistente"
            className="bg-[#1C1C1A] hover:bg-black p-5 text-left transition-colors"
            data-testid="aiuto-open-assistente"
          >
            <div className="flex items-center gap-2.5 mb-1">
              <ChatCircleDots size={16} weight="duotone" className="text-[#C8A66B]" />
              <div className="text-[10.5px] uppercase tracking-[0.2em] text-[#C8A66B] font-semibold">
                Assistente
              </div>
            </div>
            <div className="font-editorial text-xl text-white tracking-tight">
              Chiedi qualunque cosa
            </div>
            <div className="mt-1 text-[13px] text-white/70">
              Risponde subito, in italiano. Anche con la voce.
            </div>
          </Link>
        </section>

        {/* 6 guide */}
        <section className="space-y-3" data-testid="aiuto-guides-grid">
          {GUIDES.map((g) => (
            <GuideCard key={g.id} guide={g} />
          ))}
        </section>

        {/* Footer reassurance */}
        <footer className="pt-4 border-t border-[#E5E7E0] text-center text-[13px] text-[#6B6A65]">
          Le guide non bastano?
          {" "}
          <Link
            to="/dashboard/assistente"
            className="text-[#7C8B6E] hover:text-[#5e6c52] font-medium"
            data-testid="aiuto-footer-ask"
          >
            Apri l{"'"}Assistente
          </Link>
          {" "}e scrivi la tua domanda con parole tue.
        </footer>
      </div>
    </AppShell>
  );
}
